// A bipartite incidence grid — X4's concept x document matrix.
//
// THE MATRIX IS SPARSE AND MUST SAY SO. Measured on the seeded corpus: 264
// filled cells of 53 documents x 158 concepts, about 3%. Drawn without that
// number beside it, a grid of mostly-empty cells reads as "no data" rather
// than as "concepts are mostly local to one document", which is the actual
// finding. `Matrix` therefore takes a `fill` summary it renders itself rather
// than trusting each caller to remember.
//
// Ordering is the caller's business and is LAYOUT, not arithmetic: the backend
// already ranks rows by blast radius, and sorting columns by how many concepts
// touch them is what pulls the mass toward one corner instead of scattering it.
// Nothing here recomputes a value.

import { Figure } from "./Figure";
import { band } from "./scale";
import "./charts.css";

export type MatrixProps = {
  label: string;
  /** Row headers, already in the order they should be drawn. */
  rows: string[];
  /** Column headers, already ordered. Drawn rotated. */
  columns: string[];
  /** `"<row index>:<column index>"` for every filled cell. */
  filled: Set<string>;
  /** Printed under the grid — how much of the grid is actually inhabited. */
  fill: string;
  cell?: number;
  notMeasured?: { reason: string };
};

const ROW_LABEL_W = 128;
const COL_LABEL_H = 78;

export function Matrix({
  label,
  rows,
  columns,
  filled,
  fill,
  cell = 11,
  notMeasured,
}: MatrixProps) {
  const width = ROW_LABEL_W + columns.length * cell;
  const height = COL_LABEL_H + rows.length * cell;
  const cols = band(columns.length, columns.length * cell);
  const rowBand = band(rows.length, rows.length * cell);

  return (
    <div className="maisight-chart-matrix-wrap">
      <Figure label={label} width={width} height={height} notMeasured={notMeasured}>
        {columns.map((name, index) => (
          <text
            key={name}
            className="maisight-chart-matrix-col"
            transform={`translate(${ROW_LABEL_W + cols.center(index)},${COL_LABEL_H - 6}) rotate(-60)`}
          >
            {name}
          </text>
        ))}
        {rows.map((name, index) => (
          <text
            key={name}
            className="maisight-chart-matrix-row"
            x={ROW_LABEL_W - 8}
            y={COL_LABEL_H + rowBand.center(index)}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {name}
          </text>
        ))}
        {rows.map((_, row) =>
          columns.map((__, column) => {
            const on = filled.has(`${row}:${column}`);
            return (
              <rect
                key={`${row}:${column}`}
                className="maisight-chart-matrix-cell"
                data-on={on ? "yes" : undefined}
                x={ROW_LABEL_W + cols.start(column) + 1}
                y={COL_LABEL_H + rowBand.start(row) + 1}
                width={cell - 2}
                height={cell - 2}
                rx={1.5}
              />
            );
          }),
        )}
      </Figure>
      <p className="maisight-chart-note type-bodyS">{fill}</p>
    </div>
  );
}
